import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { IBlockItemFormProps } from 'views/Pages/PagesForm/constants';
import {
  imageItemSchema,
  pageFormSchema
} from 'views/Pages/PagesForm/validation-schema';
import { z } from 'zod';

import { cn } from 'components/lib/utils';
import UploadImage from 'components/UploadImage/UploadImage';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../../components/ui/form';
import { Input } from '../../../../../../../components/ui/input';

type ImageItemBlockType = z.infer<typeof imageItemSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type ImageItemFieldProps<T extends keyof ImageItemBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: ImageItemBlockType[T];
  };

export const ImageItemForm: React.FC<IBlockItemFormProps> = ({
  item,
  blockIndex,
  itemIndex
}) => {
  const {
    control,
    formState: { errors }
  } = useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}.items.${itemIndex}` as const;

  const handleFieldChange = async (
    onChange: (value: string) => void,
    value: string
  ) => {
    onChange(value);
  };

  return (
    <>
      <FormField
        control={control}
        name={`${basePath}.url`}
        render={({
          field: { onChange, value }
        }: {
          field: ImageItemFieldProps<'url'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Default Image"
                description={`Add an image to your page.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  handleFieldChange(onChange, url);
                }}
                className={cn('w-full')}
                isRequired
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-Image"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.mobileUrl`}
        render={({
          field: { onChange, value }
        }: {
          field: ImageItemFieldProps<'mobileUrl'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Mobile Image"
                description={`Add a mobile image to your page.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  errors[`${basePath}.mobileUrl` as keyof typeof errors]
                    ?.message
                    ? 'mb-0'
                    : ''
                )}
                selectedImageUrl={value}
                data-testid="PagesForm-Upload-MobileImage"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.redirectUrl`}
        render={({ field }: { field: ImageItemFieldProps<'redirectUrl'> }) => (
          <FormItem>
            <FormLabel>Redirect URL</FormLabel>
            <FormControl>
              <Input placeholder="Enter redirect URL" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
